import React, { useState } from 'react';
import { autoMergeCurrentUserAccount, AccountMergeResult } from '../utils/accountMerge';
import { auth } from '../config/firebase';

interface AccountMergeComponentProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const AccountMergeComponent: React.FC<AccountMergeComponentProps> = ({ 
  onSuccess, 
  onError 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<AccountMergeResult | null>(null);

  const handleMergeAccounts = async () => {
    setIsLoading(true);
    setResult(null);
    
    try {
      const mergeResult = await autoMergeCurrentUserAccount();
      setResult(mergeResult);
      
      if (mergeResult.success) {
        onSuccess?.();
      } else {
        onError?.(mergeResult.message);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setResult({
        success: false,
        message: errorMessage,
        error: 'MERGE_FAILED'
      });
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const currentUser = auth.currentUser;
  
  return (
    <div style={{ 
      padding: '20px', 
      margin: '20px', 
      border: '2px solid #ffc107', 
      borderRadius: '8px',
      backgroundColor: '#fff3cd',
      color: '#856404'
    }}>
      <h3 style={{ margin: '0 0 15px 0', color: '#856404' }}>
        🔄 Account Merge Available
      </h3>
      
      <p style={{ margin: '0 0 15px 0' }}>
        It looks like you're signed in with a different account ({currentUser?.email}) 
        than the one that contains your beekeeping data. 
      </p>
      
      <p style={{ margin: '0 0 15px 0' }}>
        Click the button below to automatically find and merge your existing apiaries, 
        hives, and inspections into your current Google account.
      </p>
      
      {result && (
        <div style={{ 
          padding: '10px', 
          margin: '10px 0',
          borderRadius: '4px',
          backgroundColor: result.success ? '#d4edda' : '#f8d7da',
          color: result.success ? '#155724' : '#721c24',
          border: result.success ? '1px solid #c3e6cb' : '1px solid #f5c6cb'
        }}>
          <strong>{result.success ? '✅ Success!' : '❌ Error:'}</strong>
          <p style={{ margin: '5px 0 0 0' }}>{result.message}</p>
          
          {result.success && result.mergedData && (
            <p style={{ margin: '10px 0 0 0', fontSize: '0.9em' }}>
              <strong>Data transferred:</strong> {result.mergedData.apiaries} apiaries, {' '}
              {result.mergedData.hives} hives, {result.mergedData.inspections} inspections
            </p>
          )}
        </div>
      )}
      
      <button
        onClick={handleMergeAccounts}
        disabled={isLoading}
        style={{
          backgroundColor: isLoading ? '#6c757d' : '#007bff',
          color: 'white',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '4px',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          fontSize: '16px',
          fontWeight: 'bold'
        }}
      >
        {isLoading ? '🔄 Merging Accounts...' : '🔗 Merge My Accounts'}
      </button>
      
      {isLoading && (
        <p style={{ margin: '10px 0 0 0', fontSize: '0.9em', fontStyle: 'italic' }}>
          This may take a few moments while we transfer your data...
        </p>
      )}
    </div>
  );
};

export default AccountMergeComponent;